import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { TotadminPermission } from 'data/__generated__';

import { Checkbox } from '@/components/ui/checkbox';
import { TotemInput } from 'components/TotemInput';

import {
    buildSections,
    getCheckState,
    grant,
    revoke,
    rowCodes,
    type CatalogEntry,
    type CheckState,
    type ModuleSection,
    type ResourceRow,
} from './permissionGrid';

type PermissionGridProps = {
    catalog: CatalogEntry[];
    value: string[];
    onChange: (permissions: string[]) => void;
    disabled?: boolean;
};

/**
 * La grille de composition d'un groupe : un module par section, une ressource par ligne, lecture /
 * gestion / actions en colonnes. Construite ENTIÈREMENT depuis le catalogue de l'API — aucun droit
 * n'est listé ici, un droit ajouté côté API apparaît tout seul.
 */
export function PermissionGrid({ catalog, value, onChange, disabled = false }: PermissionGridProps) {
    const [search, setSearch] = useState<string>('');
    const sections = useMemo(() => buildSections(catalog), [catalog]);
    const allRows = useMemo(() => sections.flatMap(({ rows }) => rows), [sections]);
    const selected = useMemo(() => new Set(value), [value]);

    const toggle = (codes: string[], state: CheckState) =>
        onChange(state === true ? revoke(value, codes, allRows) : grant(value, codes, allRows));

    const normalizedSearch = search.trim().toLowerCase();
    const visibleSections = sections
        .map((section) => ({
            ...section,
            rows: section.rows.filter(
                (row) =>
                    !normalizedSearch ||
                    row.label.toLowerCase().includes(normalizedSearch) ||
                    section.label.toLowerCase().includes(normalizedSearch),
            ),
        }))
        .filter(({ rows }) => rows.length > 0);

    const hasAnyPermission = value.length > 0;
    const canSignIn = selected.has(TotadminPermission.TotadminAccess);

    return (
        <Container>
            <TotemInput
                placeholder="Filtrer les ressources (ex. « commandes »)"
                value={search}
                onChange={setSearch}
                dataTest="permission-grid-search"
            />
            {hasAnyPermission && !canSignIn ? (
                <Warning>
                    Sans « Accéder au back-office », les membres de ce groupe ne pourront pas se connecter à la
                    totadmin.
                </Warning>
            ) : null}
            {visibleSections.map((section) => (
                <Section
                    key={section.module}
                    section={section}
                    selected={selected}
                    disabled={disabled}
                    onToggle={toggle}
                />
            ))}
        </Container>
    );
}

function Section({
    section,
    selected,
    disabled,
    onToggle,
}: {
    section: ModuleSection;
    selected: ReadonlySet<string>;
    disabled: boolean;
    onToggle: (codes: string[], state: CheckState) => void;
}) {
    const moduleState = getCheckState(selected, section.codes);
    const grantedCount = section.codes.filter((code) => selected.has(code)).length;

    return (
        <Table>
            {/* Largeurs fixes : les colonnes restent alignées d'un module à l'autre. */}
            <colgroup>
                <col />
                <col width={110} />
                <col width={110} />
                <col width="40%" />
            </colgroup>
            <thead>
                <tr>
                    <ModuleHeader colSpan={4}>
                        <ModuleTitle>
                            <Checkbox
                                checked={moduleState}
                                disabled={disabled}
                                onCheckedChange={() => onToggle(section.codes, moduleState)}
                                data-test={`permission-module-${section.module}`}
                                aria-label={`Tout le module ${section.label}`}
                            />
                            <span>{section.label}</span>
                            <Count>
                                {grantedCount} / {section.codes.length}
                            </Count>
                        </ModuleTitle>
                    </ModuleHeader>
                </tr>
                <tr>
                    <ColumnHeader>Ressource</ColumnHeader>
                    <ColumnHeader $centered>Consulter</ColumnHeader>
                    <ColumnHeader $centered>Gérer</ColumnHeader>
                    <ColumnHeader>Actions</ColumnHeader>
                </tr>
            </thead>
            <tbody>
                {section.rows.map((row) => (
                    <Row key={row.resource} row={row} selected={selected} disabled={disabled} onToggle={onToggle} />
                ))}
            </tbody>
        </Table>
    );
}

function Row({
    row,
    selected,
    disabled,
    onToggle,
}: {
    row: ResourceRow;
    selected: ReadonlySet<string>;
    disabled: boolean;
    onToggle: (codes: string[], state: CheckState) => void;
}) {
    const rowState = getCheckState(selected, rowCodes(row));

    return (
        <tr>
            <Cell>
                <ResourceLabel>
                    <Checkbox
                        checked={rowState}
                        disabled={disabled}
                        onCheckedChange={() => onToggle(rowCodes(row), rowState)}
                        aria-label={`Toute la ressource ${row.label}`}
                    />
                    {row.label}
                </ResourceLabel>
            </Cell>
            <Cell $centered>
                <EntryCheckbox entry={row.read} selected={selected} disabled={disabled} onToggle={onToggle} />
            </Cell>
            <Cell $centered>
                <EntryCheckbox entry={row.manage} selected={selected} disabled={disabled} onToggle={onToggle} />
            </Cell>
            <Cell>
                <Actions>
                    {row.actions.map((entry) => (
                        <ActionLabel key={entry.code}>
                            <EntryCheckbox entry={entry} selected={selected} disabled={disabled} onToggle={onToggle} />
                            {entry.label}
                        </ActionLabel>
                    ))}
                </Actions>
            </Cell>
        </tr>
    );
}

function EntryCheckbox({
    entry,
    selected,
    disabled,
    onToggle,
}: {
    entry: CatalogEntry | null;
    selected: ReadonlySet<string>;
    disabled: boolean;
    onToggle: (codes: string[], state: CheckState) => void;
}) {
    if (!entry) {
        return null;
    }

    const checked = selected.has(entry.code);

    return (
        <Checkbox
            checked={checked}
            disabled={disabled}
            onCheckedChange={() => onToggle([entry.code], checked)}
            data-test={`permission-${entry.code}`}
            aria-label={entry.label}
            title={entry.label}
        />
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const Warning = styled.div`
    padding: 10px 12px;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.warningColor};
    color: ${({ theme }) => theme.textColor};
    font-size: 14px;
`;

const Table = styled.table`
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
`;

const ModuleHeader = styled.th`
    padding: 10px 12px;
    text-align: left;
    background-color: ${({ theme }) => theme.backgroundColor};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const ModuleTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 600;
`;

const Count = styled.span`
    font-size: 13px;
    font-weight: 400;
    color: ${({ theme }) => theme.infoTextColor};
`;

const ColumnHeader = styled.th<{ $centered?: boolean }>`
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
    color: ${({ theme }) => theme.infoTextColor};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const Cell = styled.td<{ $centered?: boolean }>`
    padding: 8px 12px;
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    vertical-align: middle;

    ${({ $centered }) =>
        $centered &&
        `
        > button {
            margin: 0 auto;
        }
    `}
`;

const ResourceLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
`;

const ActionLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
`;
